<template>
  <div>
    <select-field ref="SalarieSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Salarie from '@/extend/classes/Salarie';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionSalarieExtend from '@/extend/sections/SectionSalarieExtend.vue';
  import { storeSalarie } from '@/generated/stores';
  import { storeUser } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listSalarie: Salarie[] = [];
      const tmpSalarie: Salarie = new Salarie();
      return {
        selectedSalarieId: 0,
        selectedSalarie: new Salarie(),
        tmpSalarie,
        newContext: true,
        reset: true,
        listUpdated: false,
        listSalarie
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionSalarieExtend;
        section.type = 'salarie';
        section.data = this.selectedSalarie;
        section.picto = 'SectionSalarieExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): Salarie[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listSalarie;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau salarié")); 
        const salaries = this.getItems(this.listItems, this.sectionName);
        salaries.forEach(salarie => {
          if (salarie && salarie.id > 0 ) {
            items.push(new Item(salarie.id, this.getDisplay(salarie)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const salarie = this.listSalarie.find(a => a.id == itemDefined.key);
          if (salarie && salarie.id != this.selectedSalarieId) {
            this.setSelectedSalarie(salarie);
          }
          if (item.key == 0 && this.selectedSalarieId == 0) {
            this.setSelectedSalarie(this.newSalarie());
          }
          this.setSelectedSalarieId(salarie ? salarie.id : this.selectedSalarie.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedSalarie, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedSalarie(value: Salarie) {
        this.selectedSalarie = value;
      },
      setSelectedSalarieId(id: number) {
        this.selectedSalarieId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newSalarie (): Salarie {
        this.clearStore();
        const newSalarie = storeSalarie.createNewSalarie();
        this.tmpSalarie = newSalarie;
        return newSalarie;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            Salarie.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as Salarie[];
                this.listSalarie = [];
                items.forEach((item) => {
                  this.listSalarie.push(Salarie.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpSalarie != null && this.tmpSalarie.id < 0) {
          storeSalarie.mSalarieDeleted(this.tmpSalarie.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedSalarieId = 0;
        this.selectedSalarie = new Salarie();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(salarie: Salarie[], section: String): Salarie[] {
        const items: Salarie[] = [];
        salarie.forEach((item) => {
          items.push(Salarie.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(salarie: Salarie): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(salarie: Salarie, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(salarie: Salarie) {
        const item = this.items.find(a => a.key == salarie.id);
        if (!item && salarie.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == salarie.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedSalarie = this.newSalarie();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (salarie: Salarie) {
        this.selectedSalarie = salarie;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.SalarieSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.SalarieSummary.touch();
      }
    },
  })
</script>
